import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useRef, type ReactNode } from "react";

import SessionDataBoundary, { sessionDataKey } from "../auth/SessionDataBoundary";
import { useSession } from "../auth/useSession";
import { ApiError } from "../lib/api";

type Props = {
  children: ReactNode;
};

export default function ProtectedRoute({ children }: Props) {
  const location = useLocation();
  const {
    user,
    isLoading,
    isFetching,
    accessRecheckPending,
    isError,
    error,
    retrySession,
    logout,
    logoutPending,
    logoutError,
  } = useSession();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-slate-500">
        Session wird geprüft …
      </div>
    );
  }

  if (isError) {
    return (
      <AccessErrorState
        error={error}
        isFetching={isFetching}
        retrySession={retrySession}
        logout={logout}
        logoutPending={logoutPending}
        logoutError={logoutError}
      />
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  return <SessionDataBoundary key={sessionDataKey(user)} checking={accessRecheckPending || logoutPending}>{children}</SessionDataBoundary>;
}

type AccessErrorStateProps = {
  error: unknown;
  isFetching: boolean;
  retrySession: () => Promise<unknown>;
  logout: () => Promise<unknown>;
  logoutPending: boolean;
  logoutError: unknown;
};

function AccessErrorState({
  error,
  isFetching,
  retrySession,
  logout,
  logoutPending,
  logoutError,
}: AccessErrorStateProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const authenticationRejected = error instanceof ApiError && error.status === 401;
  const noTenantAccess = error instanceof ApiError && error.status === 403;
  const tenantPaused =
    error instanceof ApiError &&
    error.status === 423 &&
    error.code === "tenant_paused";
  const tenantArchived =
    error instanceof ApiError &&
    error.status === 423 &&
    error.code === "tenant_archived";
  const tenantLocked = tenantPaused || tenantArchived;
  const heading = tenantPaused
    ? "Zugang vorübergehend pausiert"
    : tenantArchived
      ? "Arbeitsbereich archiviert"
      : authenticationRejected
        ? "Anmeldung konnte nicht bestätigt werden"
      : noTenantAccess
        ? "Kein Zugriff auf diesen Mandanten"
        : "Zugriff konnte nicht geprüft werden";

  useEffect(() => {
    headingRef.current?.focus();
  }, [heading]);

  return (
    <main className="flex min-h-full items-center justify-center bg-slate-50 px-6 py-12">
      <section
        role="alert"
        aria-labelledby="access-check-title"
        className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
      >
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {tenantLocked ? "Arbeitsbereich" : "Zugriff"}
        </p>
        <h1
          ref={headingRef}
          id="access-check-title"
          tabIndex={-1}
          className="mt-2 text-xl font-semibold text-slate-900 focus:outline-none"
        >
          {heading}
        </h1>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          {tenantPaused
            ? "Dieser Arbeitsbereich wurde vorübergehend pausiert. Sobald er wieder freigegeben wurde, können Sie den Zugang hier erneut prüfen."
            : tenantArchived
              ? "Dieser Arbeitsbereich wurde archiviert und steht in der App nicht mehr regulär zur Verfügung. Wenden Sie sich bei Fragen an einen Admin."
              : authenticationRejected
                ? "Die App konnte Ihre Anmeldung nicht bestätigen. Prüfen Sie den Zugang erneut. Falls das nicht hilft, melden Sie sich ab und erneut an."
              : noTenantAccess
                ? "Ihr Konto ist diesem Mandanten nicht zugeordnet. Wenden Sie sich an einen Administrator oder melden Sie sich mit einem anderen Konto an."
                : "Ihre Rollen und Zugriffsrechte konnten gerade nicht geladen werden. Versuchen Sie es erneut."}
        </p>
        {logoutError && !logoutPending ? (
          <p
            role="alert"
            className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
          >
            Abmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.
          </p>
        ) : null}
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void retrySession()}
            disabled={isFetching}
            className="inline-flex h-9 items-center rounded-md bg-emerald-700 px-3 text-sm font-medium text-white shadow-sm hover:bg-emerald-800 disabled:cursor-wait disabled:opacity-60"
          >
            {isFetching ? "Wird geprüft …" : "Erneut prüfen"}
          </button>
          <button
            type="button"
            onClick={() => void logout().catch(() => undefined)}
            disabled={logoutPending}
            className="inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
          >
            {logoutPending ? "Wird abgemeldet …" : "Abmelden"}
          </button>
        </div>
      </section>
    </main>
  );
}
